import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageSquare, Phone, Search, RefreshCw, User, Clock, ArrowUpRight, ArrowDownLeft, Filter } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const API_URL = getApiUrl() || '';

const ConversationsHistory = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, sms, whatsapp
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ sms: 0, whatsapp: 0, total: 0 });

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/conversations/history?type=${filter}&search=${searchTerm}`);
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations || []);
        setStats(data.stats || { sms: 0, whatsapp: 0, total: 0 });
      }
    } catch (err) {
      console.error('Erreur chargement conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, searchTerm]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    if (type === 'sms') return <Phone className="w-4 h-4 text-blue-500" />;
    if (type === 'whatsapp') return <MessageSquare className="w-4 h-4 text-green-500" />;
    return <MessageSquare className="w-4 h-4 text-gray-500" />;
  };

  const getDirectionIcon = (direction) => {
    if (direction === 'outgoing') return <ArrowUpRight className="w-4 h-4 text-orange-500" />;
    return <ArrowDownLeft className="w-4 h-4 text-blue-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Historique des Conversations</h2>
          <p className="text-slate-500">SMS Ringover et WhatsApp</p>
        </div>
        <Button onClick={fetchConversations} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">SMS Ringover</p>
                <p className="text-2xl font-bold text-blue-800">{stats.sms}</p>
              </div>
              <Phone className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">WhatsApp</p>
                <p className="text-2xl font-bold text-green-800">{stats.whatsapp}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              </div>
              <Filter className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex gap-2">
              <Button 
                variant={filter === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilter('all')}
              >
                Tous
              </Button>
              <Button 
                variant={filter === 'sms' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilter('sms')}
                className="gap-1"
              >
                <Phone className="w-3 h-3" /> SMS
              </Button>
              <Button 
                variant={filter === 'whatsapp' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilter('whatsapp')}
                className="gap-1"
              >
                <MessageSquare className="w-3 h-3" /> WhatsApp
              </Button>
            </div>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par numéro, message, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversations List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Messages ({conversations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Aucune conversation trouvée
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv, index) => (
                <div 
                  key={conv._id || index}
                  className="flex items-start gap-4 p-4 rounded-lg border hover:bg-slate-50 transition-colors"
                >
                  {/* Type & Direction */}
                  <div className="flex flex-col items-center gap-1">
                    {getTypeIcon(conv.type)}
                    {getDirectionIcon(conv.direction)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-800">
                        {conv.direction === 'outgoing' ? `→ ${conv.to_number}` : `← ${conv.from_number}`}
                      </span>
                      {conv.client_name && (
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {conv.client_name}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        conv.type === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {conv.type === 'sms' ? 'SMS' : 'WhatsApp'}
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {conv.message || conv.body || conv.text || '-'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(conv.sent_at || conv.timestamp || conv.created_at)}
                      </span>
                      {conv.user_name && (
                        <span>Par: {conv.user_name}</span>
                      )}
                      {conv.context && (
                        <span className="bg-slate-100 px-2 py-0.5 rounded">{conv.context}</span>
                      )}
                      {conv.success === false && (
                        <span className="text-red-500">Échec</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversationsHistory;
